import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { Check, ChevronRight, ClipboardList, ListChecks, Pencil, Play, X } from "lucide-react";

import { getToken, useMsalAuth } from "../../authConfig";
import { LoginContext } from "../../loginContext";
import {
    getCompanyAssessmentsApi,
    updateCompanyAssessmentsApi
} from "../../api/assessmentsApi";
import { Assessment, AssessmentsDocument, ChoiceSet, EMPTY_ASSESSMENTS_DOCUMENT } from "./assessmentsTypes";
import { cloneDocument, documentsEqual } from "./assessmentsMutations";
import { validateAssessmentsDocument } from "./assessmentsSchema";
import { AssessmentEditorModal } from "./AssessmentEditorModal";
import { AssessmentListModal } from "./AssessmentListModal";
import { AssessmentPreviewModal } from "./AssessmentPreviewModal";
import { ChoiceSetEditorModal } from "./ChoiceSetEditorModal";
import { ChoiceSetListModal } from "./ChoiceSetListModal";
import styles from "./CompanyAssessments.module.css";

function formatUpdatedAt(timestamp: number | undefined, locale: string): string {
    if (!timestamp) {
        return "";
    }
    return new Date(timestamp * 1000).toLocaleString(locale);
}

interface AssessmentsEditorProps {
    onDialogOpenChange?: (open: boolean) => void;
}

export function AssessmentsEditor({ onDialogOpenChange }: AssessmentsEditorProps = {}) {
    const { t, i18n } = useTranslation();
    const { loggedIn } = useContext(LoginContext);
    const client = useMsalAuth ? useMsal().instance : undefined;

    const [saved, setSaved] = useState<AssessmentsDocument | null>(null);
    const [draft, setDraft] = useState<AssessmentsDocument>(cloneDocument(EMPTY_ASSESSMENTS_DOCUMENT));
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [assessmentListOpen, setAssessmentListOpen] = useState(false);
    const [assessmentListMode, setAssessmentListMode] = useState<"edit" | "preview">("edit");
    const [assessmentEditorOpen, setAssessmentEditorOpen] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);

    const [choiceSetListOpen, setChoiceSetListOpen] = useState(false);
    const [choiceSetEditorOpen, setChoiceSetEditorOpen] = useState(false);
    const [selectedChoiceSet, setSelectedChoiceSet] = useState<ChoiceSet | null>(null);

    const [previewOpen, setPreviewOpen] = useState(false);

    const isSubDialogOpen =
        assessmentListOpen ||
        assessmentEditorOpen ||
        choiceSetListOpen ||
        choiceSetEditorOpen ||
        previewOpen;

    useEffect(() => {
        onDialogOpenChange?.(isSubDialogOpen);
    }, [isSubDialogOpen, onDialogOpenChange]);

    useEffect(() => {
        return () => onDialogOpenChange?.(false);
    }, [onDialogOpenChange]);

    const isDirty = useMemo(() => {
        if (!saved) {
            return !documentsEqual(draft, EMPTY_ASSESSMENTS_DOCUMENT);
        }
        return !documentsEqual(draft, saved);
    }, [draft, saved]);

    const getIdToken = useCallback(async () => {
        if (client && loggedIn) {
            try {
                return await getToken(client);
            } catch (err) {
                console.error("Failed to get auth token:", err);
                return undefined;
            }
        }
        return undefined;
    }, [client, loggedIn]);

    const loadDocument = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const idToken = await getIdToken();
            const fetched = await getCompanyAssessmentsApi(idToken);
            setSaved(fetched);
            setDraft(cloneDocument(fetched));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : t("settingsPage.errors.loadFailed");
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [getIdToken, t]);

    useEffect(() => {
        void loadDocument();
    }, [loadDocument]);

    const activeDocument = editing ? draft : saved ?? EMPTY_ASSESSMENTS_DOCUMENT;
    const previewDocument = saved ?? draft;

    const startEditing = () => {
        setDraft(cloneDocument(saved ?? EMPTY_ASSESSMENTS_DOCUMENT));
        setError(null);
        setEditing(true);
    };

    const cancelEditing = () => {
        setDraft(cloneDocument(saved ?? EMPTY_ASSESSMENTS_DOCUMENT));
        setError(null);
        setEditing(false);
        setAssessmentListOpen(false);
        setAssessmentEditorOpen(false);
        setChoiceSetListOpen(false);
        setChoiceSetEditorOpen(false);
        setSelectedAssessment(null);
        setSelectedChoiceSet(null);
    };

    const saveDocument = async () => {
        const validationError = validateAssessmentsDocument(draft);
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const idToken = await getIdToken();
            const updated = await updateCompanyAssessmentsApi(draft, idToken);
            setSaved(updated);
            setDraft(cloneDocument(updated));
            setEditing(false);
            setAssessmentListOpen(false);
            setAssessmentEditorOpen(false);
            setChoiceSetListOpen(false);
            setChoiceSetEditorOpen(false);
            setSelectedAssessment(null);
            setSelectedChoiceSet(null);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : t("settingsPage.errors.saveFailed");
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleOpenCreateEdit = () => {
        if (!editing) {
            startEditing();
        }
        setAssessmentListMode("edit");
        setAssessmentListOpen(true);
    };

    const handleOpenChoiceSets = () => {
        if (!editing) {
            startEditing();
        }
        setChoiceSetListOpen(true);
    };

    const handleOpenTryAssessment = () => {
        setAssessmentListMode("preview");
        setAssessmentListOpen(true);
    };

    const assessmentCount = activeDocument.assessments.length;
    const choiceSetCount = activeDocument.choice_sets.length;
    const updatedAt = formatUpdatedAt((saved ?? draft).updated_at, i18n.language);
    const updatedBy = (saved ?? draft).updated_by;

    return (
        <div className={styles.section}>
            <details className={styles.disclosure} open>
                <summary className={styles.summary}>
                    <ChevronRight size={16} />
                    <ClipboardList size={18} />
                    <span className={styles.summaryTitle}>
                        {t("settingsPage.company.assessments.title")}
                    </span>
                    <span className={styles.summaryMeta}>
                        {t("settingsPage.company.assessments.summary", {
                            assessments: assessmentCount,
                            choiceSets: choiceSetCount
                        })}
                    </span>
                </summary>

                <div className={styles.body}>
                    <p className={styles.description}>{t("settingsPage.company.assessments.description")}</p>

                    <div className={styles.actionsRow}>
                        {!editing ? (
                            <button type="button" className={styles.editButton} onClick={startEditing}>
                                <Pencil size={16} />
                                {t("settingsPage.account.edit")}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={cancelEditing}
                                    disabled={saving}
                                >
                                    <X size={16} />
                                    {t("settingsPage.account.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className={styles.saveButton}
                                    onClick={() => void saveDocument()}
                                    disabled={saving || !isDirty}
                                >
                                    <Check size={16} />
                                    {saving
                                        ? t("settingsPage.account.saving")
                                        : t("settingsPage.account.save")}
                                </button>
                            </>
                        )}
                    </div>

                    <div className={styles.entryActions}>
                        <button
                            type="button"
                            className={styles.entryButton}
                            onClick={handleOpenCreateEdit}
                            disabled={loading}
                        >
                            <ListChecks size={14} />
                            {t("settingsPage.company.assessments.createEditAssessment")}
                        </button>
                        <button
                            type="button"
                            className={styles.entryButton}
                            onClick={handleOpenChoiceSets}
                            disabled={loading}
                        >
                            <ClipboardList size={14} />
                            {t("settingsPage.company.assessments.createMultipleChoice")}
                        </button>
                        <button
                            type="button"
                            className={styles.entryButton}
                            onClick={handleOpenTryAssessment}
                            disabled={loading || previewDocument.assessments.length === 0}
                        >
                            <Play size={14} />
                            {t("settingsPage.company.assessments.tryAssessment")}
                        </button>
                    </div>

                    {error ? <p className={styles.error}>{error}</p> : null}

                    <p className={styles.meta}>
                        {updatedAt
                            ? t("settingsPage.company.assessments.lastUpdated", {
                                  who: updatedBy || t("settingsPage.company.unknownEditor"),
                                  when: updatedAt
                              })
                            : t("settingsPage.company.assessments.neverUpdated")}
                    </p>
                </div>
            </details>

            <AssessmentListModal
                open={assessmentListOpen}
                document={assessmentListMode === "edit" ? draft : previewDocument}
                mode={assessmentListMode}
                onOpenChange={setAssessmentListOpen}
                onSelect={assessment => {
                    setSelectedAssessment(assessment);
                    if (assessmentListMode === "edit") {
                        setAssessmentEditorOpen(true);
                    } else {
                        setPreviewOpen(true);
                    }
                }}
                onCreate={assessment => {
                    setSelectedAssessment(assessment);
                    setAssessmentEditorOpen(true);
                }}
                onDraftChange={assessmentListMode === "edit" ? setDraft : undefined}
            />

            <AssessmentEditorModal
                open={assessmentEditorOpen}
                assessment={selectedAssessment}
                document={draft}
                onOpenChange={setAssessmentEditorOpen}
                onDraftChange={setDraft}
            />

            <ChoiceSetListModal
                open={choiceSetListOpen}
                document={draft}
                onOpenChange={setChoiceSetListOpen}
                onSelect={choiceSet => {
                    setSelectedChoiceSet(choiceSet);
                    setChoiceSetEditorOpen(true);
                }}
                onDraftChange={setDraft}
            />

            <ChoiceSetEditorModal
                open={choiceSetEditorOpen}
                choiceSet={selectedChoiceSet}
                document={draft}
                onOpenChange={setChoiceSetEditorOpen}
                onDraftChange={setDraft}
            />

            <AssessmentPreviewModal
                open={previewOpen}
                assessment={selectedAssessment}
                document={previewDocument}
                onOpenChange={setPreviewOpen}
            />
        </div>
    );
}
